import React, { useState, useEffect } from 'react';
import styles from './PasswordChangeModal.module.css';

import { InnocodeLoading, InnocodeLoadingOverlay, InnocodeLoadingSpinner } from '@intsoft/innocode-core';
import { InnocodePaymentPasswordButton, InnocodePaymentPasswordDot } from '@intsoft/innocode-payment';
import { getCookie } from '../../utils/cookies/cookies';
import axios from "../../utils/api/axios";
import { ZENPAY_PAY_PASSWORD } from '../../utils/Config';

type PasswordMode = 'create' | 'modify' | 'verify';


interface ApiPasswordData {
    code: number;
    userId: string;
    isVerify: boolean;
    currentCount: string;
    maxCount: string;
    isLock: boolean;
    msg: string;
}

/* 비밀번호 입력/생성/수정 모달 */
interface PasswordChangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    mode: PasswordMode; // 'create': 생성, 'modify': 수정, 'input': 입력
    onPasswordSubmit?: (password: string) => void; // 비밀번호 입력 완료 시 호출
    userId?: string;
}

export const PasswordChangeModal: React.FC<PasswordChangeModalProps> = (props: PasswordChangeModalProps) => {
    const { mode, onPasswordSubmit, userId } = props;
    const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    const [pressedIndex, setPressedIndex] = React.useState<string | null>(null);
    const [password, setPassword] = React.useState<string>('');
    const [firstPassword, setFirstPassword] = React.useState<string>('');
    // create: first -> confirm, modify: verify -> first -> confirm
    const getInitialStep = (): 'verify' | 'first' | 'confirm' => {
        if (mode === 'create') return 'first';
        if (mode === 'modify') return 'verify';
        return 'verify'; // verify mode
    };
    const [step, setStep] = React.useState<'verify' | 'first' | 'confirm'>(getInitialStep());
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const MAX_PASSWORD_LENGTH = 6;
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string>('잠시만 기다려주세요.');

    // mode가 변경되면 step 초기화
    useEffect(() => {
        const initialStep = mode === 'create' ? 'first' : mode === 'modify' ? 'verify' : 'verify';
        setStep(initialStep);
        setPassword('');
        setFirstPassword('');
        setErrorMessage('');
    }, [mode]);

    // 모드와 단계에 따른 타이틀 및 설명 텍스트
    const getTitle = () => {
        if (mode === "create") {
            return '페이 비밀번호 생성';
        } else if (mode === "modify") {
            if (step === 'verify') return '페이 비밀번호 확인';
            if (step === 'first') return '페이 비밀번호 재설정';
            if (step === 'confirm') return '재설정 페이 비밀번호 확인';
        } else if (mode === "verify") {
            return '페이 비밀번호 입력';
        }
        return '';
    };

    const getDescription = () => {
        if (mode === "create") {
            if (step === 'first') return '새 페이 비밀번호를 입력해주세요.';
            if (step === 'confirm') return '입력했던 페이 비밀번호를 한번 더 입력해주세요.';
        } else if (mode === "modify") {
            if (step === 'verify') return '사용중인 페이 비밀번호를 입력해주세요.';
            if (step === 'first') return '변경할 페이 비밀번호를 입력해주세요.';
            if (step === 'confirm') return '변경한 페이 비밀번호를 한번 더 입력해 주세요.';
        } else if (mode === "verify") {
            return '결제 비밀번호를 입력해 주세요.';
        }
        return '';
    };

    // 숫자 버튼 클릭 핸들러
    const handleNumberClick = (num: string) => {
        if (password.length < MAX_PASSWORD_LENGTH) {
            const newPassword = password + num;
            setPassword(newPassword);
            setErrorMessage(''); // 에러 메시지 초기화

            // 6자리 입력 완료 시 처리
            if (newPassword.length === MAX_PASSWORD_LENGTH) {
                handlePasswordComplete(newPassword);
            }
        }
    };

    // 한 글자 삭제
    const handleDelete = () => {
        setPassword(prev => prev.slice(0, -1));
        setErrorMessage(''); // 에러 메시지 초기화
    };

    // 전체 삭제
    const handleDeleteAll = () => {
        setPassword('');
        setErrorMessage(''); // 에러 메시지 초기화
    };


    // 비밀번호 입력 완료 시 처리
    const handlePasswordComplete = async (completedPassword: string) => {
        if (step === 'verify') {
            // verify 단계: 기존 비밀번호 확인
            const response = await axios.get(`${ZENPAY_PAY_PASSWORD}/verify`, {
                params: {
                    password: completedPassword,
                    userId: userId || getCookie('user_id')
                }
            });

            const data: ApiPasswordData = response.data.data;
            if (data.code === 200) {
                if (mode === 'verify') {
                    // verify 모드: 바로 콜백 호출
                    setIsLoading(true);
                    setLoadingMessage("잠시만 기다려주세요.")
                    setTimeout(() => {
                        setIsLoading(false);
                        onPasswordSubmit?.(completedPassword);
                        return;
                    }, 2000);
                } else if (mode === 'modify') {
                    // modify 모드: verify 완료 후 first 단계로 이동
                    setIsLoading(true);
                    setLoadingMessage("비밀번호를 확인중입니다.\n잠시만 기다려주세요.")
                    setTimeout(() => {
                        setIsLoading(false);
                        setPassword('');
                        setStep('first');
                    }, 2000);
                }
            } else if (data.code === 400) { //검증 실패
                setPassword('');
                setErrorMessage('비밀번호가 일치하지 않습니다. 다시 입력해주세요.');
                setTimeout(() => {
                    setIsLoading(false);
                    return;
                }, 2000);
            }
            // else if(data.code === 401){ //비밀번호잠김

            // }
            return;
        }

        // first 단계: 첫 번째 비밀번호 입력
        if (step === 'first') {
            setFirstPassword(completedPassword);
            setPassword('');
            setStep('confirm');
            return;
        }

        // confirm 단계: 두 번째 입력 완료 - 일치 여부 확인
        if (firstPassword === completedPassword) {
            //비밀번호 저장로직
            //수정과 새로등록은 다르게 처리
            if (mode === 'modify') {
                //수정
                const response = await axios.post(`${ZENPAY_PAY_PASSWORD}/update`, {
                    password: completedPassword,
                    userId: userId || getCookie('user_id')
                });
            } else if (mode === 'create') {
                //새로등록
                const response = await axios.post(`${ZENPAY_PAY_PASSWORD}/add`, {
                    password: completedPassword,
                    userId: userId || getCookie('user_id')
                });
            }
            setIsLoading(true);
            setLoadingMessage("비밀번호를 저장중입니다.\n잠시만 기다려주세요.")
            setTimeout(() => {
                setIsLoading(false);
                onPasswordSubmit?.(completedPassword);
            }, 2000);
        } else {
            // 비밀번호 불일치
            setErrorMessage('비밀번호가 일치하지 않습니다. 다시 입력해주세요.');
            // 초기 상태로 리셋
            setTimeout(() => {
                setPassword('');
                setFirstPassword('');
                setStep('first');
                setErrorMessage('');
            }, 2000);
        }
    };

    // 버튼 프레스 애니메이션 헬퍼
    const handleButtonPress = (key: string, action: () => void) => {
        setPressedIndex(key);
        action();
        setTimeout(() => setPressedIndex(null), 200);
    };


    return (
        <div className={styles.layoutContainer}>
            {isLoading && <InnocodeLoadingSpinner message={loadingMessage} />}
            <div className={styles.editorHeader}>
                <div className={styles.editorHeaderCloseButton} onClick={props.onClose}>
                    <svg className="w-6 h-6 text-gray-800" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" onClick={props.onClose} style={{ cursor: 'pointer' }}>
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18 17.94 6M18 18 6.06 6" />
                    </svg>
                </div>
            </div>
            <div className={styles.scrollContentArea} >

                <div className={styles.minHeightArea}>
                    <div className={styles.contentAreaWrapper}>
                        <div className={styles.contentArea}>
                            <div className={styles.contentAreaTitle}>
                                <h1 className={`innocode-typography--headline-sm innocode-typography--bold ${styles.contentAreaTitleText}`}>
                                    {getTitle()}
                                </h1>
                                <p className={`innocode-typography--body-lg-reading innocode-typography--regular ${styles.contentAreaTitleText}`}>
                                    {getDescription()}
                                </p>
                                {errorMessage && (
                                    <p className={`innocode-typography--body-md innocode-typography--regular`} style={{ color: '#ff3b30', marginTop: '8px' }}>
                                        {errorMessage}
                                    </p>
                                )}
                            </div>
                            <div className={styles.passwordDotArea}>
                                {Array.from({ length: MAX_PASSWORD_LENGTH }).map((_, index) => (
                                    <InnocodePaymentPasswordDot
                                        key={index}
                                        status={index < password.length ? "filled" : "default"}
                                    />
                                ))}
                            </div>
                            <div className={styles.keypadArea}>
                                {numbers.map((num) => (
                                    <InnocodePaymentPasswordButton
                                        key={num}
                                        status={pressedIndex === `num-${num}` ? 'pressed' : 'default'}
                                        type="number"
                                        value={{
                                            value: num.toString(),
                                            label: num,
                                        }}
                                        onClick={() => handleButtonPress(`num-${num}`, () => handleNumberClick(num.toString()))}
                                    />
                                ))}
                                <InnocodePaymentPasswordButton
                                    status={pressedIndex === 'deleteAll' ? 'pressed' : 'default'}
                                    type="text"
                                    value={{
                                        value: 'deleteAll',
                                        label: '전체삭제',
                                    }}
                                    onClick={() => handleButtonPress('deleteAll', handleDeleteAll)}
                                />
                                <InnocodePaymentPasswordButton
                                    status={pressedIndex === 'num-0' ? 'pressed' : 'default'}
                                    type="number"
                                    value={{
                                        value: '0',
                                        label: 0,
                                    }}
                                    onClick={() => handleButtonPress('num-0', () => handleNumberClick('0'))}
                                />
                                <InnocodePaymentPasswordButton
                                    status={pressedIndex === 'delete' ? 'pressed' : 'default'}
                                    type="text"
                                    value={{
                                        value: 'delete',
                                        label: <svg className="w-6 h-6 text-gray-800" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                            <path fillRule="evenodd" d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm7.707-3.707a1 1 0 0 0-1.414 1.414L10.586 12l-2.293 2.293a1 1 0 1 0 1.414 1.414L12 13.414l2.293 2.293a1 1 0 0 0 1.414-1.414L13.414 12l2.293-2.293a1 1 0 0 0-1.414-1.414L12 10.586 9.707 8.293Z" clipRule="evenodd" />
                                        </svg>,
                                    }}
                                    onClick={() => handleButtonPress('delete', handleDelete)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};